import { FC, useEffect, useMemo } from 'react';
import { fetchDeviceDetails } from 'store';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Grid, Column } from 'styles';
import PageWrapper from 'components/UI/PageWrapper';
import { useParams } from 'react-router';
import { ConnectivityDetails, Device } from 'models/connectivity';
import { connectivityDetailsSelector } from 'store/connectivityState/connectivityState';
import DeviceInfoCard from 'components/connectivity/DeviceInfoCard';
import DeviceSoftwareHistoryCard from 'components/connectivity/DeviceSoftwareHistoryCard';
import DigitalTwinCard from 'components/connectivity/DigitalTwinCard';
import DeviceErrorCard from 'components/connectivity/DeviceErrorCard';
import { format } from 'date-fns';
import DeviceShadowCard from 'components/connectivity/DeviceShadowCard';
import { getFeatureOptionTypes } from 'utils/connectivity';
import AnnotationsCard from 'components/connectivity/AnnotationsCard';
import * as routes from 'router/Routes';
import ErrorCard from 'components/ErrorCard';

function getStartAndEndTime(deviceInfo: Device | null) {
  let startTime: string;
  let endTime: string;
  const tomorrow = getTomorrow();
  const secInAWeek = 604800000;
  const oneWeekAgoTime = Date.now() - secInAWeek;
  const oneWeekAgo = format(oneWeekAgoTime, 'yyyy-MM-dd');
  if (
    !deviceInfo ||
    deviceInfo.attributes.status === 'connected' ||
    !deviceInfo.attributes.lastConnection
  ) {
    // if never connected or connected right now, search latest week
    startTime = oneWeekAgo;
    endTime = tomorrow;
  } else if (deviceInfo.attributes.lastConnection < oneWeekAgoTime) {
    // if been last connected was more than a week ago, search from connection date and a week forward
    startTime = format(deviceInfo.attributes.lastConnection, 'yyyy-MM-dd');
    endTime = format(
      deviceInfo.attributes.lastConnection + secInAWeek,
      'yyyy-MM-dd',
    );
  } else {
    // if been last connected was less than a week ago, search from connection date until tomorrow
    startTime = format(deviceInfo.attributes.lastConnection, 'yyyy-MM-dd');
    endTime = tomorrow;
  }
  return { startTime, endTime };

  function getTomorrow() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return format(date, 'yyyy-MM-dd');
  }
}

const ConnectivtyDetailsPage: FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, deviceInfo, feature, error }: ConnectivityDetails =
    useAppSelector(connectivityDetailsSelector);
  const { id } = useParams();

  // Fetch deviceDetails if id selected
  useEffect(() => {
    if (id) {
      dispatch(fetchDeviceDetails(id));
    }
  }, [dispatch, id]);

  const shadowResult = useMemo(() => {
    if (!id || !deviceInfo?.attributes?.features) {
      return null;
    }
    return (
      <DeviceShadowCard
        id={id}
        featureOptionTypes={getFeatureOptionTypes(
          deviceInfo.attributes.features,
        )}
        defaultFeature={feature}
      />
    );
  }, [deviceInfo, id, feature]);

  const twinResult = useMemo(() => {
    if (!id || !deviceInfo) {
      return null;
    }
    return <DigitalTwinCard id={id} />;
  }, [id, deviceInfo]);

  const deviceErrorsResult = useMemo(() => {
    if (!id || !feature || feature !== 'happ-device') {
      return null;
    }
    const { startTime, endTime } = getStartAndEndTime(deviceInfo);

    return <DeviceErrorCard id={id} startTime={startTime} endTime={endTime} />;
  }, [id, feature, deviceInfo]);

  const softwareHistoryResult = useMemo(() => {
    if (!id || !deviceInfo || !feature || feature !== 'happ-device') {
      return null;
    }
    return <DeviceSoftwareHistoryCard id={id} />;
  }, [id, feature, deviceInfo]);

  // Render device info
  const deviceInfoContent = useMemo(() => {
    return <DeviceInfoCard device={deviceInfo} isLoading={isLoading} />;
  }, [deviceInfo, isLoading]);

  const annotationsResult = useMemo(() => {
    if (!id || !deviceInfo) {
      return null;
    }

    return <AnnotationsCard id={id} />;
  }, [id, deviceInfo]);

  if (!id) {
    return null;
  }
  if (error) {
    return (
      <PageWrapper>
        <ErrorCard
          navTitle="connectivity.title"
          route={routes.DEVICES}
          title={`Error fetching information for iprId ${id}`}
          errorMessage={error[0]?.detail || error[0]?.title}
        />
      </PageWrapper>
    );
  }
  return (
    <PageWrapper>
      {deviceInfoContent}
      <Grid>
        <Column $marginRight>
          {shadowResult}
          {deviceErrorsResult}          
        </Column>
        <Column>
          {softwareHistoryResult}
          {twinResult}
          {annotationsResult}
        </Column>
      </Grid>
    </PageWrapper>
  );
};

export default ConnectivtyDetailsPage;
